"use client";

import React, { useState, useMemo, useEffect } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area"
import { copyToClipboardWithMeta } from "@/components/ui/copy-button";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronDown, Trash2, Share2, Check } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate, Navigate } from "react-router-dom";

import { toast } from 'react-toastify';

import { useCompetitionContext } from "../../providers/CompetitionContext";
import { Spinner } from "../../components/loading-spinner";
import { useTranslation } from "../../hooks/useTranslation";
import { getWeekFixtureLabel } from "../../lib/weekFixtureLabel";

/* --------------------------------------------
   TYPE DEFINITIONS
-------------------------------------------- */

interface League {
  id: number | string;  // string for prono games (UUID), number for fantasy games
  name: string;
  isPublic: boolean;
  admin?: number | string;  // string for prono games
  members?: { id: number | string; name: string }[];
}

interface LeaguesData {
  totalPages: number;
  currentPage: number;
  data: League[];
}

interface LeagueMember {
  oddsuserId: number | string;  // string for prono games
  userName: string;
  rank: number | null;
  points: number | string | null;
}

interface LeagueResponse {
  league: League;
  totalPages: number;
  currentPage: number;
  data: LeagueMember[];
  leagueAdmin?: number | string;  // string for prono games
}

interface UserLeague {
  leagueId: number | string;  // string for prono games (UUID)
  leagueName: string;
  isPublic: boolean;
  pinCode?: string;
  admin?: number | string;  // string for prono games
}

interface NewlyJoinedLeagueResponse {
  data: LeagueMember[];
  pageRecords: number;
  hasMore: boolean;
  nextCursor?: string;
  message?: string;
  rankingsCalculatedAt?: string | null;
}

const LEAGUE_PAGE_SIZE = 10;

interface LeaguesProps {
  weekId: number;
  user: {
    id?: string;
    favClub?: {
      id: string | number;
      name: string;
    };
    email?: string;
    username?: string;
    token?: any;
  } | null;
}

/* --------------------------------------------
   API CALLS
--------------------------------------------- */

async function fetchLeagues(competitionFeed: string, seasonId: number, page: number): Promise<LeaguesData> {
  const response = await fetch(
    `/api/leagues?competitionFeed=${competitionFeed}&seasonId=${seasonId}&pageRecords=10&pageNumber=${page}`
  )
  if (!response.ok) {
    throw new Error('Netwerkfout leagues')
  }
  return response.json()
}

async function fetchLeaguesSearch(
  competitionFeed: string,
  seasonId: number,
  page: number,
  searchTerm: string
): Promise<LeaguesData> {
  const response = await fetch(
    `/api/leagues/search?competitionFeed=${competitionFeed}&seasonId=${seasonId}&q=${encodeURIComponent(
      searchTerm
    )}&pageRecords=10&pageNumber=${page}`
  );
  if (!response.ok) {
    throw new Error("Netwerkfout leagues");
  }
  return response.json();
}

async function fetchLeague(
  leagueId: number | string,
  page: number,
  weekId: number | null,
  competitionFeed: string,
  seasonId: number
): Promise<LeagueResponse> {
  const params = new URLSearchParams();
  params.append('pageNumber', String(page));
  params.append('pageRecords', String(LEAGUE_PAGE_SIZE));
  params.append('competitionFeed', competitionFeed);
  params.append('seasonId', String(seasonId));
  
  if (weekId !== null) {
    params.append('weekId', String(weekId));
  }

  try {
    const url = `/api/league/${leagueId}?${params.toString()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Netwerk error bij ophalen league');
    }

    const jsonResponse = await response.json();
    
    const transformed: LeagueResponse = {
      totalPages: jsonResponse.totalPages || 1,
      currentPage: jsonResponse.currentPage || page,
      league: {
        id: leagueId as any,
        name: jsonResponse.leagueName || '',
        isPublic: jsonResponse.isPublic ?? true,
        admin: jsonResponse.leagueAdmin,
      },
      data: (jsonResponse.data || []).map((item: any) => ({
        oddsuserId: item.oddsuserId || item.userId,
        userName: item.userName || item.oddsuserId || item.userId || 'Unknown',
        rank: item.rank ?? null,
        points: item.points ?? 0,
      })),
      leagueAdmin: jsonResponse.leagueAdmin,
    };
    return transformed;
  } catch (error) {
    console.error('Error fetching league:', error);
    throw error;
  }
}

async function fetchNewlyJoinedLeague(
  leagueId: number | string,
  cursor: string | null,
  weekId: number | null,
  competitionFeed: string,
  seasonId: number
): Promise<NewlyJoinedLeagueResponse> {
  const params = new URLSearchParams();
  params.append('pageRecords', String(LEAGUE_PAGE_SIZE));
  params.append('competitionFeed', competitionFeed);
  params.append('seasonId', String(seasonId));
  if (cursor) params.append('cursor', cursor);
  if (weekId !== null) params.append('weekId', String(weekId));

  const response = await fetch(`/api/league/${leagueId}/newly-joined?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Erreur réseau lors du chargement des nouveaux participants');
  }

  const jsonResponse = await response.json();
  return {
    data: (jsonResponse.data || []).map((item: any) => ({
      oddsuserId: item.oddsuserId || item.userId,
      userName: item.userName || item.oddsuserId || item.userId || 'Unknown',
      rank: item.rank ?? null,
      points: item.points ?? 0,
    })),
    pageRecords: jsonResponse.pageRecords || LEAGUE_PAGE_SIZE,
    hasMore: Boolean(jsonResponse.hasMore),
    nextCursor: jsonResponse.nextCursor,
    message: jsonResponse.message,
    rankingsCalculatedAt: jsonResponse.rankingsCalculatedAt ?? null,
  };
}

// Fetch user's leagues
async function fetchUserLeagues(
  userId: string,
  competitionFeed: string,
  seasonId: number
): Promise<UserLeague[]> {
  try {
    const response = await fetch(
      `/api/user/leagues?userId=${userId}&competitionFeed=${competitionFeed}&seasonId=${seasonId}`
    );
    if (!response.ok) {
      // If endpoint doesn't exist yet, return empty array
      if (response.status === 404) {
        return [];
      }
      throw new Error('Failed to fetch user leagues');
    }
    const data = await response.json();
    return data.leagues || [];
  } catch (error) {
    console.error('Error fetching user leagues:', error);
    return [];
  }
}

interface CreateLeagueParams {
  name: string;
  isPublic: boolean;
  pinCode?: string;
  oddsuserId: string;
  competitionFeed: string;
  seasonId: number;
}

export async function createLeagueRequest(
  params: CreateLeagueParams
): Promise<{ success: boolean; leagueId: number }> {
  const response = await fetch('/api/league', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || 'League aanmaken mislukt. Probeer opnieuw.'
    );
  }

  return response.json();
}

export async function joinLeagueRequest(params: {
  leagueId: number | string;
  pinCode?: string;
  oddsuserId: string;
  competitionFeed: string;
  seasonId: number;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/league/join', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let errorMessage = 'Error joining league';

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // Fall back to default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function leaveLeagueRequest(params: {
  leagueId: number | string;
  oddsuserId: string;
  competitionFeed: string;
  seasonId: number;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/league/leave', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let errorMessage = 'Error leaving league';

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // Fall back to default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

// New function to delete a league
export async function deleteLeagueRequest(params: {
  leagueId: number | string;
  oddsuserId: string;
  competitionFeed: string;
  seasonId: number;
}): Promise<{ success: boolean }> {
  const { leagueId, oddsuserId, competitionFeed, seasonId } = params;
  const query = new URLSearchParams({
    oddsuserId,
    competitionFeed,
    seasonId: String(seasonId),
  });
  const response = await fetch(`/api/league/${leagueId}?${query.toString()}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    let errorMessage = 'Error deleting league';

    // Try to parse the error as JSON first
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      // If JSON parsing fails, try reading it as text
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // If that also fails, we'll fall back to the default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

/* --------------------------------------------
   LEAGUES PAGE COMPONENT
-------------------------------------------- */

export default function Leagues({ weekId, user }: LeaguesProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { competitionConfig, competitionData } = useCompetitionContext();
  const { t } = useTranslation();

  // Get user ID from props
  const oddsuserId = user?.id || '';

  // Query user's leagues
  const userLeaguesQuery = useQuery<UserLeague[], Error>({
    queryKey: ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId],
    queryFn: () => fetchUserLeagues(oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId),
    enabled: !!oddsuserId,
  });

  const userLeagues = useMemo(() => {
    return userLeaguesQuery.data || [];
  }, [userLeaguesQuery.data]);

  // State for "create league" modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [pinCode, setPinCode] = useState("");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [inviteShared, setInviteShared] = useState(false);

  // State for "join league" flow
  const [joinViewOpen, setJoinViewOpen] = useState(false);
  const [leaguesPageNumber, setLeaguesPageNumber] = useState(1);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [enterPin, setEnterPin] = useState("");

  // List of user's joined leagues & the selected league to display
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | string | null>(null);

  useEffect(() => {
    const firstLeague = userLeagues[0];
    if (firstLeague && selectedLeagueId === null) {
      setSelectedLeagueId(firstLeague.leagueId);
      setLeagueTablePageNumber(1);
    }
  }, [userLeagues, selectedLeagueId]);

  // State for leave league confirmation modal
  const [leaveLeagueModalOpen, setLeaveLeagueModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{oddsuserId: string, leagueId: number | string} | null>(null);
  
  // State for delete league confirmation modal
  const [deleteLeagueModalOpen, setDeleteLeagueModalOpen] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState<number | string | null>(null);

  const currentLeague = useMemo(() => {
    return userLeagues.find((l) => String(l.leagueId) === String(selectedLeagueId))
  }, [selectedLeagueId, userLeagues]);

  // Pagination for the selected league's standings
  const [leagueTablePageNumber, setLeagueTablePageNumber] = useState(1);
  const [newlyJoinedCursor, setNewlyJoinedCursor] = useState<string | null>(null);
  const [newlyJoinedCursorHistory, setNewlyJoinedCursorHistory] = useState<(string | null)[]>([]);

  // Week selection for the league standings (Journée tab)
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null);
  const [leagueWeekInitialized, setLeagueWeekInitialized] = useState(false);
  const [activeLeagueTab, setActiveLeagueTab] = useState<"general" | "week">("general");

  useEffect(() => {
    if (leagueWeekInitialized || !competitionData) return;
    if (competitionData.deadlineInfo) {
      const { deadlineWeek, displayWeek, rosterEndWeek } = competitionData.deadlineInfo;
      const defaultWeekId =
        (typeof deadlineWeek === "number" ? deadlineWeek : null) ??
        (typeof displayWeek === "number" ? displayWeek : null) ??
        (typeof rosterEndWeek === "number" ? rosterEndWeek : null) ??
        1;
      setSelectedWeekId(defaultWeekId);
    } else {
      setSelectedWeekId(1);
    }
    setLeagueWeekInitialized(true);
  }, [competitionData, leagueWeekInitialized]);

  function leagueTabClassNames(...classes: (string | boolean | undefined)[]) {
    return classes.filter(Boolean).join(" ");
  }

  const leaguePaginationBtnClass =
    "bg-white text-black border-gray-200 hover:bg-white disabled:opacity-100 disabled:bg-white disabled:text-black disabled:border-gray-200";

  const effectiveLeagueWeekId = activeLeagueTab === "general" ? null : selectedWeekId;
  const inviteSiteUrl =
    typeof window !== "undefined" ? window.location.origin : "https://pronos.rtbf.be";

  const inviteMessage = currentLeague
    ? (() => {
        const templateKey = currentLeague.isPublic
          ? "leagues.shareText"
          : "leagues.shareTextPrivate";
        const template = t(templateKey);
        return template
          .replace("{leagueName}", currentLeague.leagueName)
          .replace("{pinCode}", currentLeague.pinCode ?? "")
          .replace("{url}", inviteSiteUrl)
          .replace("{hostname}", inviteSiteUrl.replace(/^https?:\/\//, ""));
      })()
    : "";

  useEffect(() => {
    if (!inviteShared) return;

    const timeout = window.setTimeout(() => {
      setInviteShared(false);
    }, 2000);

    return () => window.clearTimeout(timeout);
  }, [inviteShared]);

  useEffect(() => {
    setNewlyJoinedCursor(null);
    setNewlyJoinedCursorHistory([]);
  }, [selectedLeagueId, activeLeagueTab, effectiveLeagueWeekId]);

  /* --------------------------------------------
     REACT QUERY HOOKS (v5 style)
  -------------------------------------------- */

  // 1) Leagues listing for "Join a league"
  const leaguesQuery = useQuery<LeaguesData, Error>({
    queryKey: ["leagues", leaguesPageNumber, activeSearchTerm],
    queryFn: () =>
      activeSearchTerm.trim() !== ""
        ? fetchLeaguesSearch(competitionConfig.competitionFeed, competitionConfig.seasonId, leaguesPageNumber, activeSearchTerm)
        : fetchLeagues(competitionConfig.competitionFeed, competitionConfig.seasonId, leaguesPageNumber),
    // keepPreviousData: true, // keep old data while fetching new
    // or refetchOnWindowFocus: false if you prefer
  });

  // const [leagues, setLeagues] = useState<LeaguesData | null>(null)

  // useEffect(() => {
  //   fetchLeagues(1)
  //     .then(data => setLeagues(data))
  //     .catch(err => console.error(err))
  // }, [])

  // 2) Specific league data (teams) for the selected league
  const leagueQuery = useQuery<LeagueResponse | null, Error>({
    queryKey: [
      "league",
      selectedLeagueId,
      leagueTablePageNumber,
      activeLeagueTab,
      effectiveLeagueWeekId,
      competitionConfig.competitionFeed,
      competitionConfig.seasonId,
    ],
    queryFn: () => {
      if (!selectedLeagueId) return Promise.resolve(null);
      return fetchLeague(
        selectedLeagueId,
        leagueTablePageNumber,
        effectiveLeagueWeekId,
        competitionConfig.competitionFeed,
        competitionConfig.seasonId,
      );
    },
    enabled:
      !!selectedLeagueId &&
      leagueWeekInitialized &&
      (activeLeagueTab === "general" || selectedWeekId !== null),
  });

  const newlyJoinedQuery = useQuery<NewlyJoinedLeagueResponse | null, Error>({
    queryKey: [
      "leagueNewlyJoined",
      selectedLeagueId,
      activeLeagueTab,
      effectiveLeagueWeekId,
      newlyJoinedCursor,
      competitionConfig.competitionFeed,
      competitionConfig.seasonId,
    ],
    queryFn: () => {
      if (!selectedLeagueId) return Promise.resolve(null);
      return fetchNewlyJoinedLeague(
        selectedLeagueId,
        newlyJoinedCursor,
        effectiveLeagueWeekId,
        competitionConfig.competitionFeed,
        competitionConfig.seasonId,
      );
    },
    enabled:
      !!selectedLeagueId &&
      leagueWeekInitialized &&
      (activeLeagueTab === "general" || selectedWeekId !== null),
    retry: false,
  });

  // 3) Create league mutation
  const createLeagueMutation = useMutation<
    { success: boolean; leagueId: number },
    Error,
    {
      name: string;
      isPublic: boolean;
      pinCode?: string;
      oddsuserId: string;
      competitionFeed: string;
      seasonId: number;
    }
  >({
    mutationFn: createLeagueRequest,
    onSuccess: (data, variables) => {
      // Update the user leagues cache to include the newly created league
      queryClient.setQueryData(
        ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId],
        (oldData: UserLeague[] | undefined) => {
          const newLeague: UserLeague = {
            leagueId: data.leagueId,
            leagueName: variables.name,
            isPublic: variables.isPublic,
            admin: Number(oddsuserId),
          };
          return [...(oldData || []), newLeague];
        }
      );

      // Set the created league as the selected league
      setSelectedLeagueId(data.leagueId);

      // Clear modal/form values and close the modal
      setCreateModalOpen(false);
      setNewLeagueName("");
      setPinCode("");
      setIsPublic(false);

      // Revalidate user leagues data
      queryClient.invalidateQueries({ 
        queryKey: ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId] 
      });
    },
    onError: (error) => {
      const message =
        error.message === "League name already exists"
          ? t("leagues.nameAlreadyExists")
          : error.message;
      toast.error(message);
    },
  });

  // 4) Join league mutation
  const joinLeagueMutation = useMutation<
    { success: boolean },
    Error,
    { leagueId: number | string; pinCode?: string; oddsuserId: string; competitionFeed: string; seasonId: number }
  >({
    mutationFn: joinLeagueRequest,
    onSuccess: () => {
      const joinedLeagueId = selectedLeague?.id;
      if (selectedLeague) {
        // Immediately update the selected league in the dropdown
        setSelectedLeagueId(selectedLeague.id);
        setLeagueTablePageNumber(1);
        setNewlyJoinedCursor(null);
        setNewlyJoinedCursorHistory([]);
        
        // Update user leagues cache
        queryClient.setQueryData(
          ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId],
          (oldData: UserLeague[] | undefined) => {
            if (!oldData) return oldData;
            // Prevent duplicate league entries
            if (oldData.some((l) => String(l.leagueId) === String(selectedLeague.id))) {
              return oldData;
            }
            const newLeague: UserLeague = {
              leagueId: selectedLeague.id,
              leagueName: selectedLeague.name,
              isPublic: selectedLeague.isPublic,
              admin: selectedLeague.admin,
            };
            return [...oldData, newLeague];
          }
        );
      }
      setSelectedLeague(null);
      setJoinViewOpen(false);
      setEnterPin("");
      // Invalidate user leagues query to refetch updated data
      queryClient.invalidateQueries({ 
        queryKey: ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId] 
      });
      if (joinedLeagueId != null) {
        queryClient.invalidateQueries({ queryKey: ["league", joinedLeagueId] });
        queryClient.invalidateQueries({ queryKey: ["leagueNewlyJoined", joinedLeagueId] });
      }
    },
  });

  // 5) Leave league mutation
  const leaveLeagueMutation = useMutation<
    { success: boolean },
    Error,
    { leagueId: number | string; oddsuserId: string; competitionFeed: string; seasonId: number }
  >({
    mutationFn: leaveLeagueRequest,
    onSuccess: (_, variables) => {
      // Clear selected league if it was the one we just left
      if (String(selectedLeagueId) === String(variables.leagueId)) {
        setSelectedLeagueId(null);
      }

      // Update user leagues cache to remove the left league
      queryClient.setQueryData(
        ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId],
        (oldData: UserLeague[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((l) => String(l.leagueId) !== String(variables.leagueId));
        }
      );

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ 
        queryKey: ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId] 
      });
      queryClient.invalidateQueries({ queryKey: ["league", variables.leagueId] });

      toast.success(t('leagues.leaveSuccess'));
      setLeaveLeagueModalOpen(false);
      setMemberToRemove(null);
    },
    onError: (error) => {
      toast.error(`${t('leagues.leaveError')}: ${error.message}`);
      setLeaveLeagueModalOpen(false);
      setMemberToRemove(null);
    }
  });
  
  // 6) Delete league mutation
  const deleteLeagueMutation = useMutation<
    { success: boolean },
    Error,
    { leagueId: number | string; oddsuserId: string; competitionFeed: string; seasonId: number }
  >({
    mutationFn: deleteLeagueRequest,
    onSuccess: (_, variables) => {
      // Clear selected league if it was the one we just deleted
      if (String(selectedLeagueId) === String(variables.leagueId)) {
        setSelectedLeagueId(null);
      }

      // Update user leagues cache to remove the deleted league
      queryClient.setQueryData(
        ["userLeagues", oddsuserId, competitionConfig.competitionFeed, competitionConfig.seasonId],
        (oldData: UserLeague[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((l) => String(l.leagueId) !== String(variables.leagueId));
        }
      );

      // Do NOT immediately refetch userLeagues here: the DynamoDB soft-delete is
      // eventually consistent, so an immediate refetch can still return the just-deleted
      // league and revert the optimistic removal above. The optimistic update is the
      // source of truth for the dropdown; it reconciles on the next natural refetch.
      queryClient.invalidateQueries({ queryKey: ["leagues"] });

      toast.success(t('leagues.deleteSuccess'));
      setDeleteLeagueModalOpen(false);
      setLeagueToDelete(null);
    },
    onError: (error) => {
      toast.error(`${t('leagues.deleteError')}: ${error.message}`);
      setDeleteLeagueModalOpen(false);
      setLeagueToDelete(null);
    }
  });

  /* --------------------------------------------
     HANDLERS
  -------------------------------------------- */
  function handleCreateLeague() {
    if (!oddsuserId) return;
    
    createLeagueMutation.mutate({
      name: newLeagueName,
      isPublic,
      pinCode: !isPublic ? pinCode : undefined,
      oddsuserId,
      competitionFeed: competitionConfig.competitionFeed,
      seasonId: competitionConfig.seasonId,
    });
  }

  function handleOpenJoinLeague() {
    setJoinViewOpen(true);
    setLeaguesPageNumber(1);
    void leaguesQuery.refetch();
  }

  function handleJoinLeague() {
    if (!selectedLeague || !oddsuserId) return;
      
    joinLeagueMutation.mutate({
      leagueId: selectedLeague.id,
      pinCode: !selectedLeague.isPublic ? enterPin : undefined,
      oddsuserId,
      competitionFeed: competitionConfig.competitionFeed,
      seasonId: competitionConfig.seasonId,
    });
  }

  function handleLeaveLeague() {
    if (!memberToRemove || !oddsuserId) return;
    
    leaveLeagueMutation.mutate({
      leagueId: memberToRemove.leagueId,
      oddsuserId: memberToRemove.oddsuserId,
      competitionFeed: competitionConfig.competitionFeed,
      seasonId: competitionConfig.seasonId,
    });
  }

  function openLeaveLeagueModal(oddsuserId: string, leagueId: number | string | null) {
    if (leagueId === null) return;
    setMemberToRemove({ oddsuserId, leagueId });
    setLeaveLeagueModalOpen(true);
  }
  
  function handleDeleteLeague() {
    if (leagueToDelete === null || !oddsuserId) return;
    
    deleteLeagueMutation.mutate({
      leagueId: leagueToDelete,
      oddsuserId,
      competitionFeed: competitionConfig.competitionFeed,
      seasonId: competitionConfig.seasonId,
    });
  }
  
  function openDeleteLeagueModal(leagueId: number | string | null) {
    if (leagueId === null) return;
    setLeagueToDelete(leagueId);
    setDeleteLeagueModalOpen(true);
  }
  
  // Check if current user is the admin of the league
  function isLeagueAdmin(leagueAdminId?: any) {
    return oddsuserId === String(leagueAdminId);
  }

  function cancelSelectedLeague() {
    setSelectedLeague(null);
  }

  function selectLeagueForDetails(league: League) {
    setSelectedLeague(league);
  }

  function handleSelectUserLeague(leagueId: number | string) {
    setSelectedLeagueId(leagueId);
    setLeagueTablePageNumber(1);
  }

  function handleLeagueRankingTabChange(tab: "general" | "week") {
    setActiveLeagueTab(tab);
    setLeagueTablePageNumber(1);
  }

  function handleNewlyJoinedNext() {
    const nextCursor = newlyJoinedQuery.data?.nextCursor;
    if (!nextCursor) return;
    setNewlyJoinedCursorHistory((history) => [...history, newlyJoinedCursor]);
    setNewlyJoinedCursor(nextCursor);
  }

  function handleNewlyJoinedPrevious() {
    setNewlyJoinedCursorHistory((history) => {
      if (history.length === 0) return history;
      const previousCursor = history[history.length - 1] ?? null;
      setNewlyJoinedCursor(previousCursor);
      return history.slice(0, -1);
    });
  }

  const formatLeaguePoints = (points: number | string | null) => {
    const numericPoints = Number(points ?? 0);
    return Number.isFinite(numericPoints) ? Math.floor(numericPoints) : 0;
  };

  async function handleShareInvite() {
    if (!inviteMessage) return;

    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: currentLeague?.leagueName,
          text: inviteMessage,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        });
      } else {
        await copyToClipboardWithMeta(inviteMessage);
      }
      setInviteShared(true);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      await copyToClipboardWithMeta(inviteMessage);
      setInviteShared(true);
    }
  }

  // Check if current user can manage a member (is self or league admin)
  function canManageMember(memberOddsuserId: number | string, leagueAdminId?: number | string) {
    return String(memberOddsuserId) === oddsuserId || String(leagueAdminId) === oddsuserId;
  }

  const leagueTabs = [
    { name: t("ranking.tabs.general"), key: "general" as const, current: activeLeagueTab === "general" },
    { name: t("ranking.tabs.week"), key: "week" as const, current: activeLeagueTab === "week" },
  ];

  /* --------------------------------------------
     RENDER
  -------------------------------------------- */

  return (
    user ? (
    <div className="min-h-[calc(100vh-100px)] px-3 pb-20 pt-6 sm:px-4">
      <div className="mx-auto w-full max-w-6xl space-y-6">

      <section className="overflow-hidden rounded-lg bg-[var(--rtbf-blue-dark,#003E63)] text-white">
        <div className="p-4 md:p-6">
          <p className="text-md">{t('leagues.description')}</p>
        </div>
      </section>

      {/* Header Actions: Join League & Create League */}
      {user.id ? (
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
          <Button
            variant="outline"
            onClick={handleOpenJoinLeague}
            className="w-full sm:w-auto rounded-full"
          >
            {t('leagues.joinLeague')}
          </Button>
          <Button
            variant="default"
            onClick={() => setCreateModalOpen(true)}
            className="w-full sm:w-auto rounded-full bg-[#FDC300] text-black hover:bg-yellow-300"
          >
            {t('leagues.createLeague')}
          </Button>
        </div>
      ) : (
        <div className="flex w-full justify-center">
          <Button
            variant="default"
            onClick={() => navigate(`/fantasy/predictions`)}
            className="w-full sm:w-auto max-w-xs"
          >
            {t('leagues.loginFirst')}
          </Button>
        </div>
      )}

      {/* BODY SECTION: SELECT LEAGUE + RANKING-STYLE TABS & TABLE */}
      {user.id && (
        <div className="space-y-6">
          <div className="w-full">
            <label className="mb-1 block text-base font-semibold text-white">{t("leagues.yourLeagues")}</label>
            <Select
              onValueChange={(value) => handleSelectUserLeague(value)}
              value={selectedLeagueId ? String(selectedLeagueId) : ""}
            >
              <SelectTrigger className="w-full border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white">
                <SelectValue placeholder={t("leagues.selectLeaguePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {userLeagues.map((l) => (
                  <SelectItem key={l.leagueId} value={String(l.leagueId)}>
                    {l.leagueName}
                  </SelectItem>
                ))}
                {userLeagues.length === 0 && <p className="m-2 block text-sm">{t("leagues.noLeaguesYet")}</p>}
              </SelectContent>
            </Select>
          </div>

          {selectedLeagueId && leagueWeekInitialized && (
            <>
              <div className="relative grid grid-cols-1 sm:hidden">
                <select
                  value={activeLeagueTab}
                  onChange={(e) => handleLeagueRankingTabChange(e.target.value as "general" | "week")}
                  aria-label="Select a tab"
                  className="w-full appearance-none rounded-full border border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] py-2.5 pl-4 pr-10 text-base font-semibold text-white outline-none focus:ring-2 focus:ring-[#FDC300]/50"
                >
                  {leagueTabs.map((tab) => (
                    <option key={tab.key} value={tab.key} className="bg-[var(--rtbf-blue-dark,#003E63)] text-white">
                      {tab.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-white/80"
                />
              </div>

              <div className="hidden sm:block">
                <nav aria-label="Tabs" className="-mb-px flex flex-wrap gap-6 border-b border-white/20">
                  {leagueTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => handleLeagueRankingTabChange(tab.key)}
                      aria-current={tab.current ? "page" : undefined}
                      className={leagueTabClassNames(
                        tab.current
                          ? "border-[#FDC300] text-white"
                          : "border-transparent text-white/70 hover:border-white/40 hover:text-white",
                        "whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-semibold transition-colors",
                      )}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              {activeLeagueTab === "week" && (
                <div className="w-full">
                  <Select
                    value={selectedWeekId != null ? String(selectedWeekId) : ""}
                    onValueChange={(value: string) => {
                      setLeagueTablePageNumber(1);
                      setSelectedWeekId(Number(value));
                    }}
                  >
                    <SelectTrigger
                      aria-label={t("leagues.matchday")}
                      className="w-full border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] text-white"
                    >
                      <SelectValue placeholder={t("leagues.selectMatchday")} />
                    </SelectTrigger>
                    <SelectContent>
                      {(competitionData?.weeks ?? []).map((week: any) => (
                        <SelectItem key={week.weekId} value={String(week.weekId)}>
                          {getWeekFixtureLabel(week.weekId, competitionData)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="overflow-hidden rounded-md text-white">
                <Table className="min-w-full text-sm">
                  <TableHeader className="bg-[var(--rtbf-blue,#0076A9)] [&_tr]:border-[var(--rtbf-blue,#0076A9)]">
                    <TableRow className="border-b-0 hover:bg-transparent">
                      <TableHead className="w-16 py-3 font-medium text-white"></TableHead>
                      <TableHead className="py-3 font-medium text-white">{t("ranking.table.user")}</TableHead>
                      <TableHead className="py-3 text-right font-medium text-white">
                        {t("leagues.points")}
                      </TableHead>
                      <TableHead className="py-3 text-right font-medium text-white">{t("leagues.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leagueQuery.isLoading ? (
                      <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                        <TableCell colSpan={4} className="py-8 text-center text-white">
                          <div className="flex items-center justify-center gap-2">
                            <Spinner size={20} className="text-white/80" />
                            <span className="text-white/80">{t("leagues.loading")}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : !leagueQuery.data || leagueQuery.data.data.length === 0 ? (
                      <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                        <TableCell colSpan={4} className="py-6 text-center text-white/90">
                          {t("ranking.noResults")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      leagueQuery.data.data.map((member, index) => {
                        const rowBg =
                          index % 2 === 0
                            ? "bg-[var(--rtbf-blue-dark,#003E63)]"
                            : "bg-[var(--rtbf-blue,#0076A9)]";
                        const displayRank =
                          member.rank != null
                            ? member.rank
                            : "-";
                        return (
                          <TableRow
                            key={String(member.oddsuserId)}
                            className={leagueTabClassNames(
                              rowBg,
                              "border-b border-[var(--rtbf-blue,#0076A9)] text-white hover:bg-white/10",
                            )}
                          >
                            <TableCell className="font-medium text-white">{displayRank}</TableCell>
                            <TableCell className="text-white">{member.userName}</TableCell>
                            <TableCell className="text-right font-medium text-white">
                              {formatLeaguePoints(member.points)}
                            </TableCell>
                            <TableCell className="text-right text-white">
                              {canManageMember(member.oddsuserId, leagueQuery.data?.league?.admin) ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openLeaveLeagueModal(String(member.oddsuserId), selectedLeagueId)}
                                  className="text-red-400 hover:bg-white/10 hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {!leagueQuery.isLoading && leagueQuery.data && leagueQuery.data.data.length > 0 && (() => {
                const ld = leagueQuery.data;
                const tp = ld.totalPages;
                const cp = ld.currentPage;
                const len = ld.data.length;
                const totalRec = (tp - 1) * LEAGUE_PAGE_SIZE + (cp === tp ? len : LEAGUE_PAGE_SIZE);
                return (
                  <div className="flex flex-col items-center justify-end gap-4 p-2 sm:flex-row">
                    <p className="text-sm text-white/90">
                      {t("ranking.pagination.showing")}{" "}
                      <span className="font-medium text-white">{(leagueTablePageNumber - 1) * LEAGUE_PAGE_SIZE + 1}</span>{" "}
                      {t("ranking.pagination.to")}{" "}
                      <span className="font-medium text-white">
                        {Math.min(leagueTablePageNumber * LEAGUE_PAGE_SIZE, (leagueTablePageNumber - 1) * LEAGUE_PAGE_SIZE + len)}
                      </span>{" "}
                      {t("ranking.pagination.of")} <span className="font-medium text-white">{totalRec}</span>{" "}
                      {t("ranking.pagination.results")}
                    </p>
                    <nav className="flex items-center gap-2 text-sm">
                      <Button
                        variant="outline"
                        size="sm"
                        className={leaguePaginationBtnClass}
                        onClick={() => setLeagueTablePageNumber(1)}
                        disabled={leagueTablePageNumber === 1}
                      >
                        1
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={leaguePaginationBtnClass}
                        onClick={() => setLeagueTablePageNumber((p) => Math.max(1, p - 1))}
                        disabled={leagueTablePageNumber === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="min-w-[2.5rem] rounded-md bg-[var(--rtbf-blue,#0076A9)] px-3 py-1.5 text-center text-sm font-bold text-white">
                        {leagueTablePageNumber}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className={leaguePaginationBtnClass}
                        onClick={() => setLeagueTablePageNumber((p) => Math.min(tp, p + 1))}
                        disabled={leagueTablePageNumber === tp}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={leaguePaginationBtnClass}
                        onClick={() => setLeagueTablePageNumber(tp)}
                        disabled={leagueTablePageNumber === tp}
                      >
                        {tp}
                      </Button>
                    </nav>
                  </div>
                );
              })()}

              {(newlyJoinedQuery.isLoading ||
                newlyJoinedQuery.isError ||
                (newlyJoinedQuery.data?.data.length ?? 0) > 0) && (
                <div className="space-y-2 text-white">
                  <div>
                    <h2 className="text-base font-semibold">{t("leagues.newMembersTitle")}</h2>
                    <p className="text-sm text-white/90">
                      {t("leagues.newMembersMessage")}
                    </p>
                  </div>
                  <div className="overflow-hidden rounded-md text-white">
                    <Table className="min-w-full text-sm">
                      <TableHeader className="bg-[var(--rtbf-blue,#0076A9)] [&_tr]:border-[var(--rtbf-blue,#0076A9)]">
                        <TableRow className="border-b-0 hover:bg-transparent">
                          <TableHead className="w-16 py-3 font-medium text-white"></TableHead>
                          <TableHead className="py-3 font-medium text-white">{t("ranking.table.user")}</TableHead>
                          <TableHead className="py-3 text-right font-medium text-white">
                            {t("leagues.points")}
                          </TableHead>
                          <TableHead className="py-3 text-right font-medium text-white">{t("leagues.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {newlyJoinedQuery.isLoading ? (
                          <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                            <TableCell colSpan={4} className="py-8 text-center text-white">
                              <div className="flex items-center justify-center gap-2">
                                <Spinner size={20} className="text-white/80" />
                                <span className="text-white/80">{t("leagues.loading")}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : newlyJoinedQuery.isError ? (
                          <TableRow className="border-b border-[var(--rtbf-blue,#0076A9)] bg-[var(--rtbf-blue-dark,#003E63)] hover:bg-[var(--rtbf-blue-dark,#003E63)]">
                            <TableCell colSpan={4} className="py-6 text-center text-white/90">
                              Les nouveaux participants n'ont pas pu être chargés.
                            </TableCell>
                          </TableRow>
                        ) : (
                          newlyJoinedQuery.data?.data.map((member, index) => {
                            const rowBg =
                              index % 2 === 0
                                ? "bg-[var(--rtbf-blue-dark,#003E63)]"
                                : "bg-[var(--rtbf-blue,#0076A9)]";
                            return (
                              <TableRow
                                key={String(member.oddsuserId)}
                                className={leagueTabClassNames(
                                  rowBg,
                                  "border-b border-[var(--rtbf-blue,#0076A9)] text-white hover:bg-white/10",
                                )}
                              >
                                <TableCell className="font-medium text-white">-</TableCell>
                                <TableCell className="text-white">{member.userName}</TableCell>
                                <TableCell className="text-right font-medium text-white">
                                  {formatLeaguePoints(member.points)}
                                </TableCell>
                                <TableCell className="text-right text-white">
                                  {canManageMember(member.oddsuserId, leagueQuery.data?.league?.admin) ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openLeaveLeagueModal(String(member.oddsuserId), selectedLeagueId)}
                                      className="text-red-400 hover:bg-white/10 hover:text-red-300"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  {!newlyJoinedQuery.isLoading &&
                    !newlyJoinedQuery.isError &&
                    newlyJoinedQuery.data &&
                    newlyJoinedQuery.data.data.length > 0 &&
                    (newlyJoinedCursorHistory.length > 0 || newlyJoinedQuery.data.hasMore) && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className={leaguePaginationBtnClass}
                          onClick={handleNewlyJoinedPrevious}
                          disabled={newlyJoinedCursorHistory.length === 0}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className={leaguePaginationBtnClass}
                          onClick={handleNewlyJoinedNext}
                          disabled={!newlyJoinedQuery.data.hasMore}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                </div>
              )}

              {leagueQuery.data && (
                <>
                  {currentLeague && (
                    <div className="space-y-1">
                      <h2 className="text-base font-semibold text-white">{t("leagues.inviteFriends")}</h2>
                      <section className="overflow-hidden rounded-lg bg-[var(--rtbf-blue-dark,#003E63)] text-white">
                        <div className="p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-md text-white whitespace-pre-line">{inviteMessage}</p>
                            <Button
                              variant="default"
                              size="sm"
                              className="inline-flex w-full shrink-0 items-center justify-center gap-2 bg-[#FDC300] font-bold tracking-[0.08em] text-black hover:bg-yellow-300 sm:w-auto"
                              onClick={() => {
                                void handleShareInvite();
                              }}
                              title={t("leagues.shareInvite")}
                              aria-label={t("leagues.shareInvite")}
                            >
                              {inviteShared ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
                              {t("leagues.shareInvite")}
                            </Button>
                          </div>
                        </div>
                      </section>
                    </div>
                  )}

                  <div className="space-y-1 text-white">
                    <h2 className="text-base font-semibold">{t("leagues.actions")}</h2>
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openLeaveLeagueModal(oddsuserId, selectedLeagueId)}
                        className="w-full border-red-500 bg-white text-red-500 hover:bg-red-50 hover:text-red-600 sm:w-auto"
                      >
                        {t("leagues.leave")}
                      </Button>
                      {isLeagueAdmin(leagueQuery.data.league.admin) && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => openDeleteLeagueModal(selectedLeagueId)}
                          className="w-full sm:w-auto"
                        >
                          {t("leagues.deleteLeagueButton")}
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      </div>

      {/* CREATE LEAGUE MODAL */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="border-[var(--color-neutral-04)] bg-white shadow-[0_20px_50px_var(--color-rules-tour-shadow)]">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">{t('leagues.createLeague')}</DialogTitle>
          </DialogHeader>
          {/* <ScrollArea className="h-[200px] w-[350px] rounded-md border p-4"> */}
            <div className="space-y-4">
            <Input
              placeholder={t('leagues.leagueNamePlaceholder')}
              value={newLeagueName}
              onChange={(e) => setNewLeagueName(e.target.value)}
              className="border-gray-200 bg-[var(--color-neutral-01)] text-[var(--color-text-body)]"
            />
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-[var(--color-text-body)]">{t('leagues.leagueTypeLabel')}</legend>
              <label
                className={isPublic
                  ? "flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-[var(--rtbf-blue,#0076A9)] p-3 text-white"
                  : "flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-[var(--color-neutral-01)] p-3 text-[var(--color-text-body)]"
                }
              >
                <input
                  type="radio"
                  name="leagueType"
                  checked={isPublic}
                  onChange={() => setIsPublic(true)}
                  className="h-4 w-4 self-center accent-[var(--rtbf-blue,#0076A9)]"
                />
                <div className="space-y-1">
                  <p className={isPublic ? "text-sm font-semibold text-white" : "text-sm font-semibold text-[var(--color-text-body)]"}>
                    {t('leagues.publicLeague')}
                  </p>
                  <p className={isPublic ? "text-xs text-white/90" : "text-xs text-gray-600"}>
                    {t('leagues.publicLeagueDescription')}
                  </p>
                </div>
              </label>
              <label
                className={!isPublic
                  ? "flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-[var(--rtbf-blue,#0076A9)] p-3 text-white"
                  : "flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 bg-[var(--color-neutral-01)] p-3 text-[var(--color-text-body)]"
                }
              >
                <input
                  type="radio"
                  name="leagueType"
                  checked={!isPublic}
                  onChange={() => setIsPublic(false)}
                  className="h-4 w-4 self-center accent-[var(--rtbf-blue,#0076A9)]"
                />
                <div className="space-y-1">
                  <p className={!isPublic ? "text-sm font-semibold text-white" : "text-sm font-semibold text-[var(--color-text-body)]"}>
                    {t('leagues.privateLeague')}
                  </p>
                  <p className={!isPublic ? "text-xs text-white/90" : "text-xs text-gray-600"}>
                    {t('leagues.privateLeagueDescription')}
                  </p>
                </div>
              </label>
            </fieldset>
            {!isPublic && (
              <div className="space-y-2">
                <Input
                  placeholder={t('leagues.pinCodePlaceholder')}
                  maxLength={4}
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value)}
                  className="border-gray-200 bg-[var(--color-neutral-01)] text-[var(--color-text-body)]"
                />
                <p className="text-xs text-gray-600">
                  Choisissez un code à 4 chiffres pour protéger l’accès à votre ligue.
                </p>
              </div>
            )}
          </div>
          {/* </ScrollArea> */}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              {t('leagues.cancel')}
            </Button>
            <Button 
              variant="default" 
              className="bg-[#FDC300] text-black hover:bg-yellow-300"
              onClick={handleCreateLeague} 
              disabled={!newLeagueName || createLeagueMutation.isPending}
            >
              {createLeagueMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner size={16} className="text-white" />
                  {t('leagues.saving')}
                </span>
              ) : (
                t('leagues.save')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* JOIN LEAGUE OVERVIEW MODAL */}
      <Dialog open={joinViewOpen} onOpenChange={setJoinViewOpen}>
        <DialogContent className="max-w-xl border-[var(--color-neutral-04)] bg-white shadow-[0_20px_50px_var(--color-rules-tour-shadow)]">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">{t('leagues.joinLeague')}</DialogTitle>
          </DialogHeader>
            {selectedLeague && (
              <div className="p-3 border rounded-md border-[var(--color-neutral-04)] bg-[var(--color-neutral-01)]">
                <h2 className="text-base font-semibold mb-2">{selectedLeague.name}</h2>
                {/* Members in that league preview */}
                <ul className="mb-2 list-disc list-inside">
                  {selectedLeague.members?.map((m) => (
                    <li key={m.id}>{m.name}</li>
                  ))}
                </ul>
                {!selectedLeague.isPublic && (
                  <Input
                    placeholder={t('leagues.enterPin')}
                    maxLength={4}
                    value={enterPin}
                    onChange={(e) => setEnterPin(e.target.value)}
                    className="mb-2 border-[var(--color-neutral-04)] bg-[var(--color-neutral-01)] text-[var(--color-text-body)]"
                  />
                )}
                <Button variant="secondary" onClick={cancelSelectedLeague}>
                  {t('leagues.cancel')}
                </Button>
                <Button 
                  variant="default" 
                  className="m-4 bg-[#FDC300] text-black hover:bg-yellow-300" 
                  onClick={handleJoinLeague}
                  disabled={joinLeagueMutation.isPending}
                >
                  {joinLeagueMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <Spinner size={16} className="text-white" />
                      {t('leagues.joining')}
                    </span>
                  ) : (
                    t('leagues.join')
                  )}
                </Button>
              </div>
            )}
            {!selectedLeague && 
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <Input
                  placeholder={t('leagues.searchLeague')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setLeaguesPageNumber(1);
                  }}
                  className="flex-grow mr-4 border-[var(--color-neutral-04)] bg-[var(--color-neutral-01)] text-[var(--color-text-body)]"
                />
                <Button variant="default" className="ml-2 bg-[#FDC300] text-black hover:bg-yellow-300" onClick={() => setActiveSearchTerm(searchTerm.trim())} disabled={searchTerm.trim().length < 4}>
                  {t('leagues.search')}
                </Button>
              </div>
            {/* List of leagues to join */}
            {leaguesQuery.isLoading && (
              <div className="flex items-center justify-center gap-2 py-8">
                <Spinner size={20} className="text-[var(--bet-red)]" />
                <span className="text-gray-500">{t('leagues.loadingLeagues')}</span>
              </div>
            )}
            {!leaguesQuery.isLoading && leaguesQuery.data && (
              <>
                <table className="min-w-full divide-y divide-[var(--rtbf-blue,#0076A9)] text-sm">
                  <thead className="bg-[var(--rtbf-blue,#0076A9)]">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-white">{t('leagues.name')}</th>
                      <th className="px-4 py-2 text-left font-medium text-white">{t('leagues.type')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaguesQuery.data.data.map((league, index) => (
                      <tr
                        key={league.id}
                        onClick={() => selectLeagueForDetails(league)}
                        className="cursor-pointer bg-white text-gray-900 border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-4 py-2">{league.name}</td>
                        <td className="px-4 py-2">
                          {league.isPublic ? t('leagues.typePublic') : t('leagues.typePrivate')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination for league listing */}
                <div className="flex justify-end items-center p-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (leaguesPageNumber > 1) {
                        setLeaguesPageNumber((p) => p - 1);
                        void leaguesQuery.refetch();
                      }
                    }}
                    disabled={leaguesPageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    {t('leagues.pageOf').replace('{current}', String(leaguesQuery.data.currentPage)).replace('{total}', String(leaguesQuery.data.totalPages))}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (leaguesPageNumber < leaguesQuery.data.totalPages) {
                        setLeaguesPageNumber((p) => p + 1);
                        void leaguesQuery.refetch();
                      }
                    }}
                    disabled={leaguesPageNumber === leaguesQuery.data.totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}

            
          </div>}
          {/* </ScrollArea> */}
          {/* <DialogFooter>
            <Button variant="outline" onClick={() => setJoinViewOpen(false)}>
              Close
            </Button>
          </DialogFooter> */}
        </DialogContent>
      </Dialog>

      {/* LEAVE LEAGUE CONFIRMATION MODAL */}
      <Dialog open={leaveLeagueModalOpen} onOpenChange={setLeaveLeagueModalOpen}>
        <DialogContent className="border-[var(--color-neutral-04)] bg-white shadow-[0_20px_50px_var(--color-rules-tour-shadow)]">
          <div className="py-4 text-center">
            <p>{t('leagues.leaveConfirmation')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('leagues.leaveWarning')}</p>
          </div>
          <DialogFooter className="justify-center sm:justify-center">
            <Button variant="outline" onClick={() => setLeaveLeagueModalOpen(false)}>
              {t('leagues.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleLeaveLeague}
              disabled={leaveLeagueMutation.isPending}
            >
              {leaveLeagueMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner size={16} className="text-white" />
                  {t('leagues.leaving')}
                </span>
              ) : (
                t('leagues.leaveLeagueButton')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* DELETE LEAGUE CONFIRMATION MODAL */}
      <Dialog open={deleteLeagueModalOpen} onOpenChange={setDeleteLeagueModalOpen}>
        <DialogContent className="border-[var(--color-neutral-04)] bg-white shadow-[0_20px_50px_var(--color-rules-tour-shadow)]">
          <div className="py-4 text-center">
            <p className="font-semibold text-red-500">{t('leagues.deleteWarning')}</p>
            <p className="mt-2">{t('leagues.deleteConfirmation')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('leagues.deleteDescription')}</p>
          </div>
          <DialogFooter className="justify-center sm:justify-center">
            <Button variant="outline" onClick={() => setDeleteLeagueModalOpen(false)}>
              {t('leagues.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteLeague}
              disabled={deleteLeagueMutation.isPending}
            >
              {deleteLeagueMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner size={16} className="text-white" />
                  {t('leagues.deleting')}
                </span>
              ) : (
                t('leagues.deleteLeagueButtonConfirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    ) : <Navigate to="/" replace={true} />
  );
}
